import React, { useState, useEffect } from 'react';
import '../styles/StudentDocumentManager.scss';
import { track } from '@vercel/analytics';

function StudentDocumentManager({
  API_BASE_URL,
  authToken,
  userData,
  onBack,
  onQuizCreated
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadMode, setUploadMode] = useState(null); // 'file' or 'ocr'
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  // Upload states
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadSubject, setUploadSubject] = useState('');
  const [uploadTopic, setUploadTopic] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressInfo, setProgressInfo] = useState({
    status: '',
    progress: 0,
    estimated_time: '',
    files_completed: 0,
    total_files: 0
  });

  // OCR states
  const [ocrFiles, setOcrFiles] = useState([]);
  const [ocrTaskId, setOcrTaskId] = useState(null);
  const [ocrText, setOcrText] = useState('');
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [showOcrEditor, setShowOcrEditor] = useState(false);

  // Quiz generation states
  const [questionCount, setQuestionCount] = useState(10);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');

  // Shared quiz states
  const [shareIdInput, setShareIdInput] = useState('');
  const [shareIdError, setShareIdError] = useState(null);
  const [loadingShareId, setLoadingShareId] = useState(false);

  // UI states
  const [documentsCollapsed, setDocumentsCollapsed] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [groupBySubject, setGroupBySubject] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  // Predefined subjects list - different for UK and other users
  const getSubjects = () => {
    // Check if user is from the UK
    const isUKUser = userData && userData.country && (
      userData.country.toLowerCase() === 'united kingdom' ||
      userData.country.toLowerCase() === 'uk' ||
      userData.country.toLowerCase() === 'great britain' ||
      userData.country.toLowerCase() === 'britain'
    );

    if (isUKUser) {
      // UK-specific subjects (GCSE/A-Level style)
      return [
        'Science',
        'Mathematics',
        'English Language',
        'English Literature',
        'History',
        'Geography',
        'Religious Studies',
        'Art & Design Subjects',
        'Biology',
        'Chemistry',
        'Business Studies',
        'Spanish',
        'Computer Science',
        'Music',
        'Statistics',
        'Drama & Theatre Studies',
        'Design & Technology',
        'Food Preparation & Nutrition',
        'Physical Education',
        'Other'
      ];
    } else {
      // Default subjects (Irish/European style)
      return [
        'English',
        'Irish',
        'Mathematics',
        'Biology',
        'Chemistry',
        'History',
        'Geography',
        'Business',
        'Applied Mathematics',
        'Home Economics',
        'Accounting',
        'Economics',
        'Physics',
        'French',
        'Physics & Chemistry',
        'German',
        'Spanish',
        'Computer Science',
        'Physical Education',
        'Agricultural Science',
        'Engineering',
        'Construction Studies',
        'Design & Communication Graphics',
        'Technology',
        'Art',
        'Music',
        'Ancient Greek',
        'Latin',
        'Arabic',
        'Japanese',
        'Russian',
        'Hebrew',
        'Polish',
        'Portuguese',
        'Mandarin',
        'Classical Studies',
        'Other'
      ];
    }
  };

  const SUBJECTS = getSubjects();

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Fetch user's documents
  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/quiz/student/documents`, {
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      } else {
        console.error('Failed to fetch documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!uploadFiles.length) return;
    
    if (!uploadSubject.trim()) {
      alert('Please select a subject for your documents.');
      return;
    }

    // Combine subject and topic for the final subject string
    const finalSubject = uploadTopic.trim() 
      ? `${uploadSubject} - ${uploadTopic.trim()}`
      : uploadSubject;

    setUploading(true);
    setShowProgressModal(true);
    setProgressInfo({
      status: 'Starting upload...',
      progress: 0,
      estimated_time: `30 seconds`,
      files_completed: 0,
      total_files: uploadFiles.length
    });

    try {
      const taskIds = [];
      
      // Upload all files first
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        
        setProgressInfo(prev => ({
          ...prev,
          status: `Uploading ${file.name}...`,
          progress: (i / uploadFiles.length) * 20 // First 20% for uploads
        }));

        const formData = new FormData();
        formData.append('file', file);
        formData.append('subject', finalSubject);

        const response = await fetch(`${API_BASE_URL}/api/quiz/student/upload-document`, {
          method: 'POST',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        const data = await response.json();
        taskIds.push({ taskId: data.task_id, filename: file.name });
      }

      // Now track processing for all files
      setProgressInfo(prev => ({
        ...prev,
        status: 'All files uploaded! Processing documents...',
        progress: 20
      }));

      // Poll for processing completion of all files
      await Promise.all(taskIds.map(({ taskId, filename }) => 
        pollDocumentProcessing(taskId, filename)
      ));

      // All done!
      setProgressInfo(prev => ({
        ...prev,
        status: 'All documents processed successfully!',
        progress: 100,
        estimated_time: 'Complete',
        files_completed: uploadFiles.length
      }));

      // Wait a moment to show completion, then refresh and close
      setTimeout(async () => {
        await fetchDocuments();
        setUploadFiles([]);
        setUploadSubject('');
        setUploadTopic('');
        setShowUploadModal(false);
        setShowProgressModal(false);
        setUploadProgress('');
        
        track('student_document_upload_completed', {
          file_count: uploadFiles.length,
          subject: finalSubject,
          user_role: userData?.role || 'unknown'
        });
      }, 2000);

    } catch (error) {
      console.error('Upload error:', error);
      setProgressInfo(prev => ({
        ...prev,
        status: `Upload failed: ${error.message}`,
        progress: 0
      }));
      
      // Show error for a moment, then allow retry
      setTimeout(() => {
        setShowProgressModal(false);
      }, 3000);
    } finally {
      setUploading(false);
    }
  };

  // Handle OCR upload
  const handleOcrUpload = async () => {
    if (!ocrFiles.length) return;
    
    if (!uploadSubject.trim()) {
      alert('Please select a subject for your documents.');
      return;
    }

    // Combine subject and topic for the final subject string
    const finalSubject = uploadTopic.trim() 
      ? `${uploadSubject} - ${uploadTopic.trim()}`
      : uploadSubject;

    setOcrProcessing(true);
    setShowProgressModal(true);
    setProgressInfo({
      status: 'Starting OCR processing...',
      progress: 0,
      estimated_time: '1 minute',
      files_completed: 0,
      total_files: 1
    });

    try {
      const formData = new FormData();
      ocrFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('subject', finalSubject);

      setProgressInfo(prev => ({
        ...prev,
        status: 'Uploading images for OCR...',
        progress: 20
      }));

      const response = await fetch(`${API_BASE_URL}/api/quiz/student/upload-document-ocr`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to start OCR processing');
      }

      const data = await response.json();
      setOcrTaskId(data.task_id);
      
      setProgressInfo(prev => ({
        ...prev,
        status: 'Processing images with OCR...',
        progress: 40,
        estimated_time: '1 minute'
      }));
      
      // Poll for OCR completion
      const ocrResult = await pollOcrProcessing(data.task_id);
      
      console.log('OCR Result:', ocrResult); // Debug log
      
      setProgressInfo(prev => ({
        ...prev,
        status: 'OCR completed! Review the extracted text.',
        progress: 100,
        estimated_time: 'Complete'
      }));
      
      // Wait a moment then show editor
      setTimeout(() => {
        if (ocrResult && ocrResult.response) {
          setOcrText(ocrResult.response);
          setShowProgressModal(false);
          setShowOcrEditor(true);
        } else {
          console.error('Invalid OCR result:', ocrResult);
          throw new Error('Invalid OCR result - no text extracted');
        }
      }, 1500);
      
    } catch (error) {
      console.error('OCR error:', error);
      setProgressInfo(prev => ({
        ...prev,
        status: `OCR failed: ${error.message}`,
        progress: 0
      }));
      
      // Show error for a moment, then close and reset
      setTimeout(() => {
        setShowProgressModal(false);
        setOcrProcessing(false);
        alert(`OCR processing failed.\n\nPlease try again with clearer images or fewer files.`);
      }, 3000);
    }
  };

  // Save edited OCR text
  const handleSaveOcrText = async () => {
    if (!ocrText.trim()) return;

    // Combine subject and topic for the final subject string
    const finalSubject = uploadTopic.trim() 
      ? `${uploadSubject} - ${uploadTopic.trim()}`
      : uploadSubject;

    setUploading(true);
    setUploadProgress('Saving document...');

    try {
      const response = await fetch(`${API_BASE_URL}/api/quiz/student/save-edited-document`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: ocrText,
          subject: finalSubject,
          filename: 'OCR_Notes.txt'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save document');
      }

      const data = await response.json();
      setUploadProgress('Processing document...');
      
      // Poll for processing completion
      await pollDocumentProcessing(data.task_id);
      
      // Refresh documents and reset state
      await fetchDocuments();
      setOcrText('');
      setOcrFiles([]);
      setUploadSubject('');
      setUploadTopic('');
      setShowOcrEditor(false);
      setShowUploadModal(false);
      setOcrProcessing(false);
      setUploadProgress('');
      
      track('student_ocr_document_saved', {
        subject: finalSubject,
        text_length: ocrText.length,
        user_role: userData?.role || 'unknown'
      });

    } catch (error) {
      console.error('Save error:', error);
      alert(`Save failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Poll for document processing completion
  const pollDocumentProcessing = async (taskId, filename) => {
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/quiz/student/task-status/${taskId}`, {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          });

          if (!response.ok) {
            throw new Error('Failed to check processing status');
          }

          const data = await response.json();
          
          // Update progress info with current status
          setProgressInfo(prev => ({
            ...prev,
            status: `${filename}: ${data.status}`,
            progress: Math.max(prev.progress, 20 + (data.progress || 0) * 0.6), // Scale progress to 20-80%
            estimated_time: data.estimated_time || prev.estimated_time
          }));
          
          if (data.state === 'SUCCESS') {
            setProgressInfo(prev => ({
              ...prev,
              files_completed: prev.files_completed + 1,
              status: `${filename}: Completed! (${data.facts_extracted || 0} facts extracted)`
            }));
            resolve(data.result);
          } else if (data.state === 'FAILURE') {
            reject(new Error(`Processing failed for ${filename}: ${data.error}`));
          } else {
            // Still processing, check again in 2 seconds
            setTimeout(checkStatus, 2000);
          }
        } catch (error) {
          reject(error);
        }
      };
      checkStatus();
    });
  };

  // Poll for OCR processing completion
  const pollOcrProcessing = async (taskId) => {
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/task-status/${taskId}`, {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          });

          if (!response.ok) {
            throw new Error('Failed to check OCR status');
          }

          const data = await response.json();
          
          // Update progress info with current status
          setProgressInfo(prev => ({
            ...prev,
            status: data.status || 'Processing OCR...',
            progress: Math.min(prev.progress + 5, 90) // Gradually increase progress
          }));
          
          if (data.state === 'SUCCESS' && data.result && data.result.status === 'success') {
            resolve(data.result);
          } else if (data.state === 'FAILURE' || (data.result && data.result.status === 'error')) {
            const errorMsg = data.result?.error || data.error || 'OCR processing failed';
            reject(new Error(errorMsg));
          } else {
            // Still processing, check again in 2 seconds
            setTimeout(checkStatus, 2000);
          }
        } catch (error) {
          reject(error);
        }
      };
      checkStatus();
    });
  };

  // Handle generating quiz from selected documents
  const handleGenerateQuizFromSelected = async () => {
    const processedDocs = documents.filter(doc => 
      doc.processed && doc.processing_status === 'completed'
    );

    // If documents are selected, use those; otherwise use all processed documents
    const docsToUse = selectedDocuments.length > 0 
      ? processedDocs.filter(doc => selectedDocuments.includes(doc.id))
      : processedDocs;

    if (docsToUse.length === 0) {
      alert('No processed documents available. Please upload and process some documents first.');
      return;
    }

    // Generate subject string from selected documents
    const uniqueSubjects = [...new Set(
      docsToUse.map(doc => doc.subject).filter(Boolean)
    )].sort();
    
    const generatedSubject = uniqueSubjects.join(' & ');

    if (!generatedSubject) {
      alert('Selected documents do not have valid subjects.');
      return;
    }

    setGenerating(true);
    setGenerationProgress('Starting quiz generation...');

    try {
      const response = await fetch(`${API_BASE_URL}/api/quiz/student/generate-quiz-from-notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        },
        body: JSON.stringify({
          subject: generatedSubject,
          document_ids: docsToUse.map(doc => doc.id),
          question_count: questionCount
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate quiz: ${response.status}`);
      }

      const data = await response.json();
      setGenerationProgress('Quiz generated successfully!');
      
      // Call the onQuizCreated callback to navigate to the quiz
      if (onQuizCreated) {
        onQuizCreated(data.quiz_id);
      }
      
    } catch (error) {
      console.error('Error generating quiz:', error);
      alert(`Failed to generate quiz: ${error.message}`);
    } finally {
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  // Handle loading quiz by Share ID
  const handleLoadShareId = async () => {
    if (shareIdInput.length !== 5) {
      setShareIdError('Share ID must be 5 characters long.');
      return;
    }
    setShareIdError(null);
    setLoadingShareId(true);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/quiz/by-share-id/${shareIdInput.toUpperCase()}`,
        {
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
          }
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to load quiz by share ID: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.id) {
        // Quiz loaded successfully - call the callback with quiz ID
        onQuizCreated(data.id);
      } else {
        throw new Error('Invalid response from server when loading by share ID.');
      }
    } catch (err) {
      console.error('Error loading quiz by Share ID:', err);
      setShareIdError(err.message || 'Failed to load quiz. Please check the ID and try again.');
    } finally {
      setLoadingShareId(false);
    }
  };

  // Get processed documents
  const processedDocuments = documents.filter(doc => 
    doc.processed && doc.processing_status === 'completed'
  );

  // Filter documents based on search
  const filteredDocuments = processedDocuments.filter(doc => {
    if (!searchFilter.trim()) return true;
    
    const searchTerm = searchFilter.toLowerCase();
    return (
      doc.filename.toLowerCase().includes(searchTerm) ||
      (doc.subject && doc.subject.toLowerCase().includes(searchTerm))
    );
  });

  // Group documents by subject if grouping is enabled
  const groupedDocuments = groupBySubject 
    ? filteredDocuments.reduce((groups, doc) => {
        const subject = doc.subject || 'Uncategorized';
        if (!groups[subject]) {
          groups[subject] = [];
        }
        groups[subject].push(doc);
        return groups;
      }, {})
    : null;

  // Delete document function
  const handleDeleteDocument = async (docId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/quiz/student/documents/${docId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      // Remove from selected documents if it was selected
      setSelectedDocuments(prev => prev.filter(id => id !== docId));
      
      // Refresh documents list
      await fetchDocuments();
      
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(`Failed to delete document: ${error.message}`);
    }
  };

  return (
    <div className="student-document-manager">
      <button onClick={onBack} className="back-button">
        ← Back to Subjects
      </button>
      
      <h2>My Study Notes</h2>
      <h3>Upload your notes and create custom quizzes for any subject!</h3>

      {/* How-To Guide Button */}
      <div className="how-to-section">
        <button 
          onClick={() => setShowHowTo(true)}
          className="how-to-button"
        >
          ❓ How do I use this?
        </button>
      </div>

      {/* Upload Section */}
      <div className="upload-section">
        <h3>Upload New Documents</h3>
        <div className="upload-buttons">
          <button 
            onClick={() => { setUploadMode('file'); setShowUploadModal(true); }}
            className="upload-btn file-upload"
          >
            📄 Upload Files (.txt, .docx)
          </button>
          <button 
            onClick={() => { setUploadMode('ocr'); setShowUploadModal(true); }}
            className="upload-btn ocr-upload"
          >
            📷 Upload Images (OCR)
          </button>
        </div>
      </div>

      {/* Documents List */}
      <div className="documents-section">
        <div className="section-header">
          <h3>My Notes ({filteredDocuments.length})</h3>
          <div className="header-controls">
            <button 
              onClick={() => setGroupBySubject(!groupBySubject)}
              className={`group-toggle ${groupBySubject ? 'active' : ''}`}
            >
              {groupBySubject ? '📋 List View' : '📁 Group by Subject'}
            </button>
            <button 
              onClick={() => setDocumentsCollapsed(!documentsCollapsed)}
              className="collapse-toggle"
            >
              {documentsCollapsed ? '▼ Show ' : '▲ Hide '}
            </button>
          </div>
        </div>
        
        {!documentsCollapsed && (
          <>
            {processedDocuments.length > 0 && (
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search documents by name or subject..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="search-input"
                />
                {searchFilter && (
                  <button 
                    onClick={() => setSearchFilter('')}
                    className="clear-search"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
            
            {loading ? (
              <p>Loading documents...</p>
            ) : processedDocuments.length === 0 ? (
              <p className="no-documents">No documents uploaded yet. Upload some notes to get started!</p>
            ) : filteredDocuments.length === 0 ? (
              <p className="no-documents">No documents match your search. Try a different term.</p>
            ) : groupBySubject ? (
              // Grouped view
              <div className="grouped-documents">
                {Object.entries(groupedDocuments).sort().map(([subject, docs]) => (
                  <div key={subject} className="subject-group">
                    <h4 className="subject-header">
                      {subject} ({docs.length})
                    </h4>
                    <div className="documents-grid">
                      {docs.map(doc => (
                        <div key={doc.id} className="document-card">
                          <div className="document-info">
                            <h4>{doc.subject || 'Uncategorized'}</h4>
                            <small className="document-filename" style={{ fontSize: '0.65em' }}>({doc.filename})</small>
                          </div>
                          <div className="document-actions">
                            <input
                              type="checkbox"
                              checked={selectedDocuments.includes(doc.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDocuments([...selectedDocuments, doc.id]);
                                } else {
                                  setSelectedDocuments(selectedDocuments.filter(id => id !== doc.id));
                                }
                              }}
                            />
                            <button 
                              onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                              className="delete-btn"
                              title="Delete document"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Regular view
              <div className="documents-grid">
                {filteredDocuments.map(doc => (
                  <div key={doc.id} className="document-card">
                    <div className="document-info">
                      <h4>{doc.subject || 'Uncategorized'}</h4>
                      <small className="document-filename" style={{ fontSize: '0.65em' }}>({doc.filename})</small>
                    </div>
                    <div className="document-actions">
                      <input
                        type="checkbox"
                        checked={selectedDocuments.includes(doc.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDocuments([...selectedDocuments, doc.id]);
                          } else {
                            setSelectedDocuments(selectedDocuments.filter(id => id !== doc.id));
                          }
                        }}
                      />
                      <button 
                        onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                        className="delete-btn"
                        title="Delete document"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Generate Quiz Section */}
      {processedDocuments.length > 0 && (
        <div className="quiz-generation-section">
          <h3>Generate Quiz from Your Notes</h3>
          <p>Create a personalized quiz from your uploaded documents.</p>
          
          {/* Document Selection Summary */}
          <div className="selection-summary">
            <h4>Documents to Include</h4>
            {selectedDocuments.length > 0 ? (
              <div>
                <p><strong>{selectedDocuments.length}</strong> documents selected:</p>
                <div className="selected-docs-list">
                  {selectedDocuments.map(docId => {
                    const doc = documents.find(d => d.id === docId);
                    return doc ? (
                      <div key={docId} className="selected-doc-item">
                        <span className="doc-name">{doc.filename}</span>
                        <span className="doc-subject">({doc.subject})</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            ) : (
              <p>All {processedDocuments.length} processed documents will be used.</p>
            )}
          </div>

          {/* Quiz Configuration */}
          <div className="quiz-config">
            <div className="config-row">
              <div className="config-item">
                <label>Quiz Subject:</label>
                <div className="subject-display">
                  {(() => {
                    // Generate subject from selected documents
                    const docsToUse = selectedDocuments.length > 0 
                      ? processedDocuments.filter(doc => selectedDocuments.includes(doc.id))
                      : processedDocuments;
                    
                    const uniqueSubjects = [...new Set(
                      docsToUse.map(doc => doc.subject).filter(Boolean)
                    )].sort();
                    
                    const generatedSubject = uniqueSubjects.join(' & ');
                    
                    return generatedSubject || 'No subjects available';
                  })()}
                </div>
              </div>
              
              <div className="config-item">
                <label htmlFor="question-count">Number of Questions:</label>
                <select
                  id="question-count"
                  value={questionCount}
                  onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                  className="question-count-select"
                >
                  <option value={5}>5 questions</option>
                  <option value={10}>10 questions</option>
                  <option value={15}>15 questions</option>
                  <option value={20}>20 questions</option>
                </select>
              </div>
            </div>
          </div>

          {/* Generation Progress */}
          {generationProgress && (
            <div className="generation-progress">
              <p>{generationProgress}</p>
            </div>
          )}

          {/* Generate Button */}
          <div className="generate-button-section">
            <button 
              onClick={handleGenerateQuizFromSelected}
              className="generate-quiz-btn"
              disabled={generating}
            >
              {generating ? 'Generating Quiz...' : `Generate ${questionCount} Question Quiz`}
            </button>
          </div>
        </div>
      )}

      {/* Try Shared Quiz Section */}
      <div className="shared-quiz-section">
        <h3>Try a Shared Quiz</h3>
        <p>Enter a 5-character share ID to try a quiz shared by someone else.</p>
        <div className="share-id-input-group">
          <input 
            type="text" 
            value={shareIdInput}
            onChange={(e) => setShareIdInput(e.target.value.toUpperCase())}
            placeholder="e.g. ABC12"
            maxLength={5}
            className="share-id-input"
            disabled={loadingShareId}
          />
          <button 
            onClick={handleLoadShareId} 
            className="load-share-id-button"
            disabled={loadingShareId || shareIdInput.length !== 5}
          >
            {loadingShareId ? 'Loading...' : 'Try Quiz'}
          </button>
        </div>
        {shareIdError && <p className="error-message share-id-error">{shareIdError}</p>}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{uploadMode === 'file' ? 'Upload Files' : 'Upload Images for OCR'}</h3>
            
            <div className="upload-form">
              <div className="form-group">
                <label>Subject:</label>
                <select
                  value={uploadSubject}
                  onChange={(e) => setUploadSubject(e.target.value)}
                >
                  <option value="">Select a subject...</option>
                  {SUBJECTS.map(subject => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Topic (optional):</label>
                <input
                  type="text"
                  value={uploadTopic}
                  onChange={(e) => setUploadTopic(e.target.value)}
                  placeholder="e.g., Organic Chemistry, Photosynthesis, etc."
                  maxLength={Math.max(1, 50 - uploadSubject.length - 3)}
                />
               
              </div>

              <div className="form-group">
                <label>
                  {uploadMode === 'file' ? 'Select Files (.txt, .docx):' : 'Select Images (.jpg, .png, .pdf):'}
                </label>
                <input
                  type="file"
                  multiple
                  accept={uploadMode === 'file' ? '.txt,.docx' : '.jpg,.jpeg,.png,.pdf'}
                  onChange={(e) => {
                    if (uploadMode === 'file') {
                      setUploadFiles(Array.from(e.target.files));
                    } else {
                      setOcrFiles(Array.from(e.target.files));
                    }
                  }}
                />
              </div>

              {uploadProgress && (
                <div className="upload-progress">
                  <p>{uploadProgress}</p>
                </div>
              )}

              <div className="modal-actions">
                <button 
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFiles([]);
                    setOcrFiles([]);
                    setUploadSubject('');
                    setUploadTopic('');
                    setUploadProgress('');
                  }}
                  disabled={uploading || ocrProcessing}
                >
                  Cancel
                </button>
                <button 
                  onClick={uploadMode === 'file' ? handleFileUpload : handleOcrUpload}
                  disabled={
                    uploading || 
                    ocrProcessing || 
                    (uploadMode === 'file' ? !uploadFiles.length : !ocrFiles.length)
                  }
                >
                  {uploading || ocrProcessing ? 'Processing...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {showProgressModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Processing Documents</h3>
            
            <div className="progress-content">
              <div className="progress-status">
                <p className="status-text">{progressInfo.status}</p>
                {progressInfo.estimated_time && (
                  <p className="time-estimate">
                    Estimated time remaining: {progressInfo.estimated_time}
                  </p>
                )}
              </div>
              
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${progressInfo.progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{Math.round(progressInfo.progress)}%</span>
              </div>
              
              {progressInfo.total_files > 1 && (
                <div className="files-progress">
                  <p>Files completed: {progressInfo.files_completed} / {progressInfo.total_files}</p>
                </div>
              )}
              
              <div className="progress-info">
                <p>✨ Your documents are being analyzed by Pulc to extract key facts and concepts</p>
                <p>📚 Once complete, you'll be able to generate personalized quizzes from your notes</p>
                <p>⏱️ If it's taking too long, try a shorter document</p>
              </div>
            </div>
            
            {progressInfo.progress === 100 && (
              <div className="modal-actions">
                {ocrTaskId && !showOcrEditor ? (
                  <button 
                    onClick={() => {
                      // Manual transition to OCR editor
                      setShowProgressModal(false);
                      setShowOcrEditor(true);
                      if (!ocrText) {
                        setOcrText('Please wait while we extract text from your images...');
                      }
                    }}
                    className="proceed-btn"
                  >
                    Review Extracted Text
                  </button>
                ) : (
                  <button 
                    onClick={() => setShowProgressModal(false)}
                    className="close-btn"
                  >
                    Close
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* OCR Editor Modal */}
      {showOcrEditor && (
        <div className="modal-overlay">
          <div className="modal large">
            <h3>Edit Extracted Text</h3>
            <p>Review and edit the text extracted from your images:</p>
            
            <textarea
              value={ocrText}
              onChange={(e) => setOcrText(e.target.value)}
              className="ocr-text-editor"
              rows={15}
              placeholder="Extracted text will appear here..."
            />
            
            <div className="modal-actions">
              <button 
                onClick={() => {
                  setShowOcrEditor(false);
                  setOcrText('');
                  setOcrFiles([]);
                  setUploadSubject('');
                  setUploadTopic('');
                  setOcrProcessing(false);
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveOcrText}
                disabled={uploading || !ocrText.trim()}
              >
                {uploading ? 'Saving...' : 'Save Document'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How-To Guide Modal */}
      {showHowTo && (
        <div className="modal-overlay">
          <div className="modal large how-to-modal">
            <h3>📚 How to Create Quizzes from Your Notes</h3>
            
            <div className="how-to-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>📄 Upload Your Notes</h4>
                  <p>Click "Upload Files" to add your study notes (.txt or .docx files) or "Upload Images" to use OCR on photos of your notes.</p>
                </div>
              </div>
              
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>⚡ Automatic Processing</h4>
                  <p>Pulc will automatically analyze your documents to extract key facts, concepts, and information. This usually takes 1-3 minutes per document.</p>
                  
                </div>
              </div>
              
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>🎯 Generate Custom Quizzes</h4>
                  <p>Once your documents are processed, create personalized quizzes from your notes:</p>
                  
                </div>
              </div>
              
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h4>📊 Get Detailed Feedback</h4>
                  <p>After completing a quiz, you'll receive your score and detailed feedback on each answer, as well as the correct answers and explanations.</p>
                  
                </div>
              </div>

              <div className="step">
                <div className="step-number">5</div>
                <div className="step-content">
                  <h4>🔗 Share Your Quizzes</h4>
                  <p>After creating a quiz, you can share it with friends or classmates using the Share ID.</p>
                  
                </div>
              </div>
            </div>
            
            
            <div className="modal-actions">
              <button 
                onClick={() => setShowHowTo(false)}
                className="close-btn"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentDocumentManager; 